import { NgModule } from '@angular/core';
import { PreloadAllModules, RouterModule, Routes } from '@angular/router';
import { authGuard } from './guards/auth/auth-guard';
import { onlineGuard } from './guards/onlineGuard/online-guard';
import { dashboardResolver } from './resolvers/dashboard-resolver';
import { unauthGuard } from './guards/unauth/unauth-guard';
import { puiGuard } from './guards/pre-use-inspection/pui-guard';
import { vehicleAssignedGuard } from './guards/vehicle-assigned/vehicle-assigned-guard';
import { AuthMainComponent } from './components/features/auth/auth-main/auth-main.component';

const routes: Routes = [
  {
    path: '',
    redirectTo: 'dashboard',
    pathMatch: 'full',
  },
  {
    path: 'dashboard',
    loadChildren: () =>
      import('./pages/dashboard/dashboard.module').then(
        (m) => m.DashboardPageModule
      ),
    canActivate: [authGuard],
    resolve: [dashboardResolver],
  },
  {
    path: '',
    component: AuthMainComponent,
    children: [
      {
        path: 'login',
        loadChildren: () =>
          import('./pages/login-v2/login-v2.module').then(m => m.LoginV2PageModule),
        canActivate: [unauthGuard],
      },
      {
        path: 'request-registration',
        loadChildren: () =>
          import('./pages/register/register.module').then(m => m.RegisterPageModule),
        canActivate: [unauthGuard],
      },
      {
        path: 'registration/:token',
        loadChildren: () => import('./pages/set-password/set-password.module').then(m => m.SetPasswordPageModule),
      },
      {
        path: "forgot-password-challenge/:token",
        loadChildren: () => import('./pages/set-password/set-password.module').then(m => m.SetPasswordPageModule),
      }
    ],
  },
  {
    path: 'standard-sso-web',
    loadChildren: () =>
      import('./pages/standard-sso-web/standard-sso-web.module').then(
        (m) => m.StandardSsoWebPageModule
      ),
  },
  {
    path: 'standard-sso',
    loadChildren: () =>
      import('./pages/standard-sso/standard-sso.module').then(
        (m) => m.StandardSsoPageModule
      ),
  },
  {
    path: 'saml-request',
    loadChildren: () =>
      import('./pages/saml-request/saml-request.module').then(
        (m) => m.SamlRequestPageModule
      ),
  },
  {
    path: 'saml',
    loadChildren: () =>
      import('./pages/saml/saml.module').then((m) => m.SamlPageModule),
  },
  {
    path: 'privacy-policy',
    loadChildren: () =>
      import('./pages/privacy-policy/privacy-policy.module').then(
        (m) => m.PrivacyPolicyPageModule
      ),
  },
  {
    path: 'vehicle-details',
    loadChildren: () =>
      import('./pages/vehicle-details/vehicle-details.module').then(
        (m) => m.VehicleDetailsPageModule
      ),
    canActivate: [authGuard, onlineGuard, vehicleAssignedGuard],
  },
  {
    path: 'add-new-vehicle',
    loadChildren: () =>
      import('./pages/add-new-vehicle/add-new-vehicle.module').then(
        (m) => m.AddNewVehiclePageModule
      ),
    canActivate: [authGuard, onlineGuard],
  },
  {
    path: 'unroadworthy-defects/:icnNo',
    loadChildren: () =>
      import('./pages/unroadworthy-defects/unroadworthy-defects.module').then(
        (m) => m.UnroadworthyDefectsPageModule
      ),
    canActivate: [authGuard, onlineGuard, vehicleAssignedGuard],
  },
  {
    path: 'not-my-vehicle/:icnNo',
    loadChildren: () =>
      import('./pages/not-my-vehicle/not-my-vehicle.module').then(
        (m) => m.NotMyVehiclePageModule
      ),
    canActivate: [authGuard, onlineGuard, vehicleAssignedGuard],
  },
  {
    path: 'not-using-my-vehicle',
    loadChildren: () => import('./pages/not-using-my-vehicle/not-using-my-vehicle.module').then(
      (m) => m.NotUsingMyVehiclePageModule
    ),
    canActivate: [authGuard, onlineGuard, vehicleAssignedGuard],
  },
  {
    path: 'feedback',
    loadChildren: () =>
      import('./pages/feedback/feedback.module').then(
        (m) => m.FeedbackPageModule
      ),
    canActivate: [authGuard, onlineGuard],
  },
  {
    path: 'add-vehicle-reallocation',
    loadChildren: () =>
      import(
        './pages/add-vehicle-reallocation/add-vehicle-reallocation.module'
      ).then((m) => m.AddVehicleReallocationPageModule),
    canActivate: [authGuard, onlineGuard, vehicleAssignedGuard],
  },
  {
    path: 'vehicle-reallocation',
    loadChildren: () =>
      import('./pages/vehicle-reallocation/vehicle-reallocation.module').then(
        (m) => m.VehicleReallocationPageModule
      ),
    canActivate: [authGuard, onlineGuard, vehicleAssignedGuard],
  },
  {
    path: 'contact',
    loadChildren: () =>
      import('./pages/contact/contact.module').then((m) => m.ContactPageModule),
    canActivate: [authGuard, onlineGuard, vehicleAssignedGuard],
  },
  {
    path: 'roadside-assistance',
    loadChildren: () =>
      import('./pages/roadside-assistance/roadside-assistance.module').then(
        (m) => m.RoadsideAssistancePageModule
      ),
    canActivate: [authGuard, onlineGuard, vehicleAssignedGuard],
  },
  {
    path: 'web/not-my-vehicle/:key',
    loadChildren: () =>
      import('./pages/not-my-vehicle-web/not-my-vehicle-web.module').then(
        (m) => m.NotMyVehicleWebPageModule
      ),
  },
  {
    path: 'success',
    loadChildren: () =>
      import('./pages/success/success.module').then((m) => m.SuccessPageModule),
    canActivate: [authGuard, onlineGuard],
  },
  {
    path: 'open-defects/:icnNo',
    loadChildren: () =>
      import('./pages/open-defects/open-defects.module').then(
        (m) => m.OpenDefectsPageModule
      ),
    canActivate: [authGuard, onlineGuard, vehicleAssignedGuard],
  },
  {
    path: 'add-new-pre-use-inspection/:reportId',
    loadChildren: () =>
      import(
        './pages/add-new-pre-use-inspection/add-new-pre-use-inspection.module'
      ).then((m) => m.AddNewPreUseInspectionPageModule),
    canActivate: [
       authGuard,
       (route, state) => route.queryParamMap.get('registrationNo')
         ? true
         : vehicleAssignedGuard(route, state),
       puiGuard,
     ],
  },
  {
    path: 'review-defects',
    loadChildren: () =>
      import('./pages/review-defects/review-defects.module').then(
        (m) => m.ReviewDefectsPageModule
      ),
    canActivate: [authGuard, onlineGuard, vehicleAssignedGuard],
  },
  {
    path: 'add-new-trailer',
    loadChildren: () =>
      import('./pages/add-new-trailer/add-new-trailer.module').then(
        (m) => m.AddNewTrailerPageModule
      ),
    canActivate: [authGuard, onlineGuard],
  },
  {
    path: 'inspect-hire-vehicle',
    loadChildren: () =>
      import('./pages/register-hire-vehicle/register-hire-vehicle.module').then(
        (m) => m.RegisterHireVehiclePageModule
      ),
    canActivate: [authGuard, onlineGuard],
  },
  {
    path: 'select-trailer',
    loadChildren: () =>
      import('./pages/select-trailer/select-trailer.module').then(
        (m) => m.SelectTrailerPageModule
      ),
    canActivate: [authGuard, onlineGuard, vehicleAssignedGuard],
  },
  {
    path: 'pre-use-inspection',
    loadChildren: () =>
      import('./pages/pre-use-inspection/pre-use-inspection.module').then(
        (m) => m.PreUseInspectionPageModule
      ),
    canActivate: [authGuard, vehicleAssignedGuard],
  },
  {
    path: 'not-assign-to-vehicle',
    loadChildren: () =>
      import('./pages/not-assign-to-vehicle/not-assign-to-vehicle.module').then(
        (m) => m.NotAssignToVehiclePageModule
      ),
    canActivate: [authGuard, onlineGuard],
  },
  {
    path: 'lez-caz',
    loadChildren: () =>
      import('./pages/emission-zone/emission-zone.module').then(
        (m) => m.EmissionZonePageModule
      ),
    canActivate: [authGuard, onlineGuard, vehicleAssignedGuard],
  },
  {
    path: 'damage-report',
    loadChildren: () =>
      import('./pages/damage-report/damage-report.module').then(
        (m) => m.DamageReportPageModule
      ),
    canActivate: [authGuard, onlineGuard, vehicleAssignedGuard],
  },
  {
    path: 'appointments',
    loadChildren: () =>
      import('./pages/appointments/appointments.module').then(
        (m) => m.AppointmentsPageModule
      ),
    canActivate: [authGuard, onlineGuard, vehicleAssignedGuard],
  },
  {
    path: 'documents-forms',
    loadChildren: () =>
      import('./pages/documents-forms/documents-forms.module').then(
        (m) => m.DocumentsFormsPageModule
      ),
    canActivate: [authGuard, onlineGuard],
  },
  {
    path: 'notifications/:icnNo/:value',
    loadChildren: () =>
      import('./pages/notifications/notifications.module').then(
        (m) => m.NotificationsPageModule
      ),
    canActivate: [authGuard, onlineGuard],
  },
  {
    path: 'rectify-defects/:icnNo',
    loadChildren: () =>
      import('./pages/rectify-defects/rectify-defects.module').then(
        (m) => m.RectifyDefectsPageModule
      ),
    canActivate: [authGuard, onlineGuard, vehicleAssignedGuard],
  },
  {
    path: 'rectify-add-signature',
    loadChildren: () =>
      import('./pages/rectify-add-signature/rectify-add-signature.module').then(
        (m) => m.RectifyAddSignaturePageModule
      ),
    canActivate: [authGuard, onlineGuard, vehicleAssignedGuard],
  },
  {
    path: 'rectify-detail-signature',
    loadChildren: () =>
      import(
        './pages/rectify-detail-signature/rectify-detail-signature.module'
      ).then((m) => m.RectifyDetailSignaturePageModule),
    canActivate: [authGuard, onlineGuard, vehicleAssignedGuard],
  },
  {
    path: 'rectify-pdf-upload',
    loadChildren: () =>
      import('./pages/rectify-pdf-upload/rectify-pdf-upload.module').then(
        (m) => m.RectifyPdfUploadPageModule
      ),
    canActivate: [authGuard, onlineGuard, vehicleAssignedGuard],
  },
  {
    path: 'rectify-pdf-submit',
    loadChildren: () =>
      import('./pages/rectify-pdf-submit/rectify-pdf-submit.module').then(
        (m) => m.RectifyPdfSubmitPageModule
      ),
    canActivate: [authGuard, onlineGuard, vehicleAssignedGuard],
  },
  {
    path: 'workshops',
    loadChildren: () => import('./pages/garage-service/garage-service.module').then(m => m.GarageServicePageModule),
    canActivate: [authGuard, onlineGuard, vehicleAssignedGuard]
  },
  {
    path: 'vehicle-order',
    loadChildren: () => import('./pages/vehicle-order/vehicle-order-module').then(m => m.VehicleOrderModule),
    canActivate: [authGuard, onlineGuard],
  },
  {
    path: 'picker-screen',
    loadChildren: () => import('./pages/ancillary-picker-screen/ancillary-picker-screen.module').then((m) => m.AncillaryPickerScreenPageModule),
    canActivate: [authGuard, onlineGuard, vehicleAssignedGuard]
  },
  {
    path: 'get-directions-page',
    loadChildren: () => import('./pages/get-directions/get-directions.module').then((m) => m.GetDirectionsPageModule),
    canActivate: [authGuard, onlineGuard, vehicleAssignedGuard]
  },
  {
    path: 'schedule-service-appointment/:icnNo',
    loadChildren: () => import('./pages/schedule-appointment/schedule-appointment.module').then((m) => m.ScheduleAppointmentPageModule),
    canActivate: [authGuard, onlineGuard]
  },
  {
    path: 'not-my-vehicle-web-version-confirmation',
    loadChildren: () =>
      import(
        './pages/not-my-vehicle-web-version-confirmation/not-my-vehicle-web-version-confirmation.module'
      ).then((m) => m.NotMyVehicleWebVersionConfirmationPageModule),
  },
  {
    path: 'not-my-vehicle-web-error',
    loadChildren: () =>
      import(
        './pages/not-my-vehicle-web-error/not-my-vehicle-web-error.module'
      ).then((m) => m.NotMyVehicleWebErrorPageModule),
  },
  {
    path: 'error',
    loadChildren: () =>
      import('./pages/error/error.module').then((m) => m.ErrorPageModule),
  },
  {
    path: 'faq',
    loadChildren: () =>
      import('./pages/faq/faq.module').then((m) => m.FaqPageModule),
    canActivate: [authGuard, onlineGuard],
  },
  {
    path: 'my-profile',
    loadChildren: () =>
      import('./pages/my-profile/my-profile.module').then(
        (m) => m.MyProfilePageModule
      ),
    canActivate: [authGuard, onlineGuard],
  },
  {
    path: 'settings',
    loadChildren: () =>
      import('./pages/settings/settings.module').then(
        (m) => m.SettingsPageModule
      ),
    canActivate: [authGuard, onlineGuard],
  },
  {
    path: 'cashless-parking',
    loadChildren: () =>
      import('./pages/cashless-parking/cashless-parking.module').then(
        (m) => m.CashlessParkingPageModule
      ),
    canActivate: [authGuard, onlineGuard, vehicleAssignedGuard],
  },
  {
    path: 'service-card',
    loadChildren: () =>
      import('./pages/fuel-card/fuel-card.module').then(
        (m) => m.FuelCardPageModule
      ),
    canActivate: [authGuard, onlineGuard, vehicleAssignedGuard],
  },
  {
    path: 'request-booking',
    loadChildren: () =>
      import('./pages/request-service/request-service.module').then(
        (m) => m.RequestServicePageModule
      ),
    canActivate: [authGuard, onlineGuard, vehicleAssignedGuard],
  },
  {
    path: 'booking-concern',
    loadChildren: () =>
      import('./pages/booking-concern/booking-concern.module').then(
        (m) => m.BookingConcernPageModule
      ),
    canActivate: [authGuard, onlineGuard, vehicleAssignedGuard],
  },
  {
    path: 'damage-and-safety',
    loadChildren: () =>
      import('./pages/damage-and-safety/damage-and-safety.module').then(
        (m) => m.DamageAndSafetyPageModule
      ),
    canActivate: [authGuard, onlineGuard, vehicleAssignedGuard],
  },
  {
    path: 'location',
    loadChildren: () =>
      import('./pages/vehicle-location/vehicle-location.module').then(
        (m) => m.VehicleLocationPageModule
      ),
    canActivate: [authGuard, onlineGuard, vehicleAssignedGuard],
  },
  {
    path: 'preferred-dates',
    loadChildren: () =>
      import('./pages/preferred-dates/preferred-dates.module').then(
        (m) => m.PreferredDatesPageModule
      ),
    canActivate: [authGuard, onlineGuard, vehicleAssignedGuard],
  },
  {
    path: 'booking-summary',
    loadChildren: () =>
      import('./pages/booking-summary/booking-summary.module').then(
        (m) => m.BookingSummaryPageModule
      ),
    canActivate: [authGuard, onlineGuard, vehicleAssignedGuard],
  },
  {
    path: 'booking-vendor-service',
    loadChildren: () =>
      import(
        './pages/booking-vendor-service/booking-vendor-service.module'
      ).then((m) => m.BookingVendorServicePageModule),
    canActivate: [authGuard, onlineGuard, vehicleAssignedGuard],
  },
  {
    path: 'vendor-collection-delivery',
    loadChildren: () =>
      import(
        './pages/vendor-collection-delivery/vendor-collection-delivery.module'
      ).then((m) => m.VendorCollectionDeliveryPageModule),
    canActivate: [authGuard, onlineGuard, vehicleAssignedGuard],
  },
  {
    path: 'call-holman',
    loadChildren: () =>
      import('./pages/call-holman/call-holman.module').then(
        (m) => m.CallHolmanPageModule
      ),
    canActivate: [authGuard, onlineGuard, vehicleAssignedGuard],
  },
  {
    path: 'odometer-entries',
    loadChildren: () =>
      import('./pages/odometer-entries/odometer-entries.module').then(
        (m) => m.OdometerEntriesPageModule
      ),
    canActivate: [authGuard, onlineGuard, vehicleAssignedGuard],
  },
  {
    path: 'fines',
    loadChildren: () =>
      import('./pages/fines/fines.module').then((m) => m.FinesPageModule),
    canActivate: [authGuard, onlineGuard, vehicleAssignedGuard],
  },
  {
    path: 'upload-fine',
    loadChildren: () =>
      import('./pages/upload-fine/upload-fine.module').then(
        (m) => m.UploadFinePageModule
      ),
    canActivate: [authGuard, onlineGuard, vehicleAssignedGuard],
  },
  {
    path: 'personal-use-declaration',
    loadChildren: () => import('./pages/personal-use-declaration/personal-use-declaration.module').then(
      (m) => m.PersonalUseDeclarationPageModule
    ),
    canActivate: [authGuard, onlineGuard, vehicleAssignedGuard],
  },
  {
    path: 'saml-acs/:id/:email',
    loadChildren: () =>
      import('./pages/saml-acs/saml-acs.module').then(
        (m) => m.SamlAcsPageModule
      ),
    canActivate: [unauthGuard],
  },
  {
    path: 'LoginSSO/:value',
    loadChildren: () =>
      import('./pages/login-with-sso/login-with-sso.module').then(
        (m) => m.LoginWithSsoPageModule
      ),
    canActivate: [unauthGuard]
  },
  {
    path: 'auth/callback/web',
    loadChildren: () => import('./pages/callback-web/callback-web.module').then(m => m.CallbackWebPageModule),
  },
  {
    path: 'auth/callback/mobile',
    loadChildren: () => import('./pages/callback-mobile/callback-mobile.module').then(m => m.CallbackMobilePageModule),
  },
  {
    path: '404',
    loadChildren: () =>
      import('./pages/not-found/not-found.module').then(
        (m) => m.NotFoundPageModule
      ),
  },
  {
    path: 'saml-v2',
    loadChildren: () => import('./pages/saml-v2/saml-v2.module').then(m => m.SamlV2PageModule)
  },
  { path: '**', redirectTo: '404' },

];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, { preloadingStrategy: PreloadAllModules }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule { }
